import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { promptDialog } from '@/components/dialogs/DialogHost';
import {
  Scale, Loader2, CheckCircle2, AlertCircle, MessageSquare, Send,
  FileText, ThumbsUp, ThumbsDown, Clock, Printer,
} from 'lucide-react';
import { formatDateTime } from './kit/time';
import { publicLinkError } from './errors';
import PickSelect from './shared/PickSelect';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const NAVY = '#070B5F';
const GREEN = '#6BA82E';

export default function AttorneyReviewPage() {
  const { token } = useParams();
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const [newComment, setNewComment] = useState('');
  const [commentKind, setCommentKind] = useState('note');
  const [commentTarget, setCommentTarget] = useState('doc'); // 'doc' | 'packet'
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await publicApi.get(`/attorney/review/${token}`);
      setReview(data);
      if (data.docs?.length && !selectedDoc) setSelectedDoc(data.docs[0].id);
    } catch (e) {
      setError(publicLinkError(e, 'This link is invalid or expired.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    if (!authorName.trim()) { toast.error('Please enter your name first'); return; }
    setPosting(true);
    try {
      await publicApi.post(`/attorney/review/${token}/comment`, {
        author_name: authorName,
        doc_id: commentTarget === 'packet' ? null : selectedDoc,
        comment: newComment,
        kind: commentKind,
      });
      setNewComment('');
      setCommentKind('note');
      toast.success('Comment sent to broker');
      load();
    } catch (e) {
      toast.error(publicLinkError(e, 'Failed to post comment'));
      // 409: someone already decided this review; reload so the page shows it closed.
      if (e.response?.status === 409) load();
    } finally {
      setPosting(false);
    }
  };

  const decide = async (decision) => {
    if (!authorName.trim()) { toast.error('Please enter your name first'); return; }
    const approve = decision === 'approve';
    const summary = await promptDialog({
      title: approve ? 'Approve this worksheet' : 'Request changes',
      description: 'Your note is visible to the broker.',
      label: approve ? 'Approval note (optional)' : 'What changes are needed?',
      multiline: true,
      allowEmpty: approve,
      confirmText: approve ? 'Approve' : 'Request changes',
    });
    if (summary === null) return;
    try {
      await publicApi.post(`/attorney/review/${token}/decision`, {
        author_name: authorName,
        decision,
        summary,
      });
      toast.success(decision === 'approve' ? 'Marked as approved' : 'Changes requested');
      load();
    } catch (e) {
      toast.error(publicLinkError(e, 'Failed to submit decision'));
      if (e.response?.status === 409) load();
    }
  };

  // "Approve this doc" only makes sense for a single document.
  const chooseTarget = (target) => {
    setCommentTarget(target);
    if (target === 'packet' && commentKind === 'approve_doc') setCommentKind('note');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading review…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <AlertCircle size={40} className="mx-auto text-red-500" />
          <h1 className="mt-4 font-serif text-2xl font-bold" style={{ color: NAVY }}>Review unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const currentDoc = review.docs.find((d) => d.id === selectedDoc) || review.docs[0];
  const docComments = review.comments.filter((c) => c.doc_id === (currentDoc?.id));
  const generalComments = review.comments.filter((c) => !c.doc_id);
  const finalized = review.status === 'approved' || review.status === 'changes_requested';
  const docStates = review.doc_states || {};
  const approvedCount = review.docs.filter((d) => docStates[d.id] === 'approved').length;
  const allApproved = review.docs.length > 0 && approvedCount === review.docs.length;
  const kindOptions = [
    { value: 'note', label: 'Note' },
    { value: 'change_request', label: 'Change request' },
    ...(commentTarget === 'doc' ? [{ value: 'approve_doc', label: 'Approve this doc' }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="attorney-review-page">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3 flex-wrap">
          <Scale size={22} style={{ color: GREEN }} />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Compliance review</div>
            <div className="font-serif text-lg font-bold" style={{ color: NAVY }}>
              {review.company_name}
              <span className="text-slate-400 font-normal text-sm ml-2">
                · {review.docs.length} document{review.docs.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <StatusPill status={review.status} />
          <button
            onClick={() => window.print()}
            data-testid="print-review"
            className="h-8 px-3 rounded-md border border-slate-200 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-slate-50"
            style={{ color: NAVY }}
          >
            <Printer size={13} /> Print
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 grid lg:grid-cols-[280px_1fr] gap-6 print:hidden">
        {/* Sidebar */}
        <aside className="space-y-3">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Sent by</div>
            <div className="text-sm font-semibold" style={{ color: NAVY }}>{review.sent_by_name}</div>
            <div className="text-xs text-slate-500">{review.sent_by_email}</div>
            <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
              <Clock size={11} /> {formatDateTime(review.created_at)}
            </div>
            {review.message_from_broker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Note from broker</div>
                <p className="text-sm text-slate-700 italic">"{review.message_from_broker}"</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="doc-nav">
            <div className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100">
              Documents
            </div>
            {review.docs.map((d) => {
              const active = d.id === currentDoc?.id;
              const cmtCount = review.comments.filter((c) => c.doc_id === d.id).length;
              return (
                <button
                  key={d.id}
                  onClick={() => { setSelectedDoc(d.id); setCommentKind('note'); }}
                  data-testid={`doc-nav-${d.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 transition ${
                    active ? 'bg-slate-50' : 'hover:bg-slate-50'
                  }`}
                  style={active ? { borderLeft: `3px solid ${GREEN}` } : { borderLeft: '3px solid transparent' }}
                >
                  <div className="flex items-start gap-2">
                    <FileText size={14} className="mt-0.5 shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate" style={{ color: NAVY }}>{d.title}</div>
                      <div className="text-[11px] text-slate-400 font-mono truncate">{d.required_by}</div>
                    </div>
                    <DocStateChip state={docStates[d.id]} id={d.id} />
                    {cmtCount > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                            style={{ background: '#e0e7ff', color: '#3730a3' }}>{cmtCount}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {!finalized && (
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Final decision</div>
              <div className="text-xs text-slate-600 mb-2" data-testid="approval-rollup">
                {approvedCount} of {review.docs.length} documents approved
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => decide('approve')}
                  disabled={!allApproved}
                  title={allApproved ? undefined : 'Approve every document first'}
                  data-testid="decision-approve"
                  className="text-sm px-3 h-9 rounded-md text-white font-medium inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: GREEN }}
                >
                  <ThumbsUp size={13} /> Approve
                </button>
                <button
                  onClick={() => decide('request_changes')}
                  data-testid="decision-changes"
                  className="text-sm px-3 h-9 rounded-md text-white font-medium inline-flex items-center justify-center gap-1"
                  style={{ background: '#dc2626' }}
                >
                  <ThumbsDown size={13} /> Changes
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2" data-testid="approval-hint">
                {allApproved
                  ? 'Broker will be notified either way.'
                  : 'Approve each document (use "Approve this doc") to unlock Approve. Broker will be notified either way.'}
              </p>
            </div>
          )}

          {finalized && (
            <div className="rounded-xl p-4 border" data-testid="decision-card" style={{
              background: review.status === 'approved' ? '#dcfce7' : '#fee2e2',
              borderColor: review.status === 'approved' ? '#86efac' : '#fca5a5',
            }}>
              <div className="flex items-center gap-2">
                {review.status === 'approved' ? <CheckCircle2 size={16} className="text-emerald-700" /> : <AlertCircle size={16} className="text-red-700" />}
                <div className="text-sm font-semibold" style={{ color: review.status === 'approved' ? '#166534' : '#991b1b' }}>
                  {review.status === 'approved' ? 'Approved' : 'Changes requested'}
                </div>
              </div>
              {review.decision_summary && <p className="text-xs mt-1.5" style={{ color: review.status === 'approved' ? '#166534' : '#991b1b' }}>{review.decision_summary}</p>}
              {review.decision_by && (
                <div className="text-[11px] text-slate-500 mt-1" data-testid="decision-by">
                  Decided by {review.decision_by}{review.reviewed_at && ` on ${formatDateTime(review.reviewed_at)}`}
                </div>
              )}
            </div>
          )}
        </aside>

        {/* Main */}
        <main className="space-y-4">
          {currentDoc && (
            <>
              <div className="bg-white rounded-xl border border-slate-100">
                <div className="p-5 border-b border-slate-100">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">{currentDoc.required_by}</div>
                  <h2 className="font-serif text-xl font-bold mt-1" style={{ color: NAVY }} data-testid="current-doc-title">{currentDoc.title}</h2>
                  {docStates[currentDoc.id] && docStates[currentDoc.id] !== 'pending' && (
                    <div className="mt-1.5"><DocStateChip state={docStates[currentDoc.id]} id={`${currentDoc.id}-current`} /></div>
                  )}
                  <p className="text-sm text-slate-600 mt-1">{currentDoc.summary}</p>
                </div>
                <div className="p-6 prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700">{currentDoc.body}</pre>
                </div>
              </div>

              {/* Comments on this doc */}
              <div className="bg-white rounded-xl border border-slate-100" data-testid="comments-section">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                  <MessageSquare size={14} style={{ color: NAVY }} />
                  <span className="text-sm font-semibold" style={{ color: NAVY }}>
                    Comments on this document ({docComments.length})
                  </span>
                </div>
                <div className="p-5 space-y-3">
                  {docComments.map((c) => <CommentCard key={c.id} c={c} />)}
                  {docComments.length === 0 && <div className="text-xs text-slate-400">No comments yet on this document.</div>}

                  {!finalized && (
                    <div className="mt-4 border-t border-slate-100 pt-4">
                      <Input
                        type="text"
                        value={authorName}
                        onChange={(e) => setAuthorName(e.target.value)}
                        placeholder="Your name (attorney)"
                        data-testid="comment-author"
                        className="mb-2"
                      />
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={3}
                        placeholder="Add a note or change request for the broker…"
                        data-testid="comment-body"
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      />
                      <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-slate-600" role="radiogroup" aria-label="Comment on" data-testid="comment-target">
                        <span className="font-semibold uppercase tracking-wider text-slate-500">Comment on</span>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="comment-target" value="doc" checked={commentTarget === 'doc'}
                                 onChange={() => chooseTarget('doc')} data-testid="comment-target-doc" />
                          This document
                        </label>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="comment-target" value="packet" checked={commentTarget === 'packet'}
                                 onChange={() => chooseTarget('packet')} data-testid="comment-target-packet" />
                          Whole packet
                        </label>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <div className="w-44">
                          <PickSelect
                            value={commentKind}
                            onChange={setCommentKind}
                            options={kindOptions}
                            testId="comment-kind"
                          />
                        </div>
                        <button
                          onClick={postComment}
                          disabled={posting || !newComment.trim()}
                          data-testid="comment-submit"
                          className="ml-auto text-sm px-4 h-9 rounded-md text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
                          style={{ background: NAVY }}
                        >
                          {posting ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
                          Post comment
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* General comments */}
          {generalComments.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100">
              <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold" style={{ color: NAVY }}>General comments</div>
              <div className="p-5 space-y-3">
                {generalComments.map((c) => <CommentCard key={c.id} c={c} />)}
              </div>
            </div>
          )}
        </main>
      </div>

      <PrintRecord review={review} />
    </div>
  );
}

const PRINT_KIND = { note: 'Note', change_request: 'Change request', approve_doc: 'Approved this document' };
const PRINT_DECISION = { approved: 'Approved', changes_requested: 'Changes requested' };

// What the browser prints: every document, every comment and the decision, in one flow.
// A record of the review, not a signature: typed names are labelled as such.
function PrintRecord({ review }) {
  const docStates = review.doc_states || {};
  const titleOf = (id) => review.docs.find((d) => d.id === id)?.title || id;
  const decided = review.status === 'approved' || review.status === 'changes_requested';
  return (
    <div className="hidden print:block p-6 text-sm text-black" data-testid="print-record">
      <h1 className="text-xl font-bold">Attorney review record · {review.company_name}</h1>
      <p>Sent by {review.sent_by_name} ({review.sent_by_email}) on {formatDateTime(review.created_at)}</p>
      {review.message_from_broker && <p>Note from sender: {review.message_from_broker}</p>}
      <h2 className="text-base font-bold mt-4">Decision</h2>
      {decided ? (
        <>
          <p>{PRINT_DECISION[review.status]}</p>
          {review.decision_summary && <p>{review.decision_summary}</p>}
          <p>
            Decided by {review.decision_by} (name entered by reviewer)
            {review.reviewed_at && ` on ${formatDateTime(review.reviewed_at)}`}
          </p>
        </>
      ) : (
        <p>No decision yet.</p>
      )}
      <h2 className="text-base font-bold mt-4">Comments</h2>
      {review.comments.length === 0 && <p>No comments.</p>}
      {review.comments.map((c) => (
        <div key={c.id} className="mb-2">
          <div className="text-xs">
            {c.author_name} (name entered by reviewer) · {PRINT_KIND[c.kind] || c.kind} ·{' '}
            {c.doc_id ? titleOf(c.doc_id) : 'Whole packet'} · {formatDateTime(c.created_at)}
          </div>
          <p className="whitespace-pre-wrap">{c.comment}</p>
        </div>
      ))}
      {review.docs.map((d) => (
        <section key={d.id} className="mt-6" style={{ breakBefore: 'page' }}>
          <h2 className="text-base font-bold">{d.title}</h2>
          <div className="text-xs">
            {d.required_by}
            {docStates[d.id] && ` · Reviewer standing: ${docStates[d.id].replace('_', ' ')}`}
          </div>
          <p>{d.summary}</p>
          <pre className="whitespace-pre-wrap font-sans text-sm mt-2">{d.body}</pre>
        </section>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: { bg: '#eff6ff', fg: '#1e40af', label: 'Awaiting review' },
    opened: { bg: '#fef3c7', fg: '#92400e', label: 'In review' },
    commented: { bg: '#e0e7ff', fg: '#3730a3', label: 'Comments in progress' },
    approved: { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
    changes_requested: { bg: '#fee2e2', fg: '#991b1b', label: 'Changes requested' },
  };
  const s = map[status] || map.sent;
  return (
    <span className="text-[11px] font-bold uppercase px-3 py-1 rounded" style={{ background: s.bg, color: s.fg }} data-testid="status-pill">
      {s.label}
    </span>
  );
}

const DOC_STATE_STYLE = {
  approved: { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
  changes_requested: { bg: '#fee2e2', fg: '#991b1b', label: 'Changes' },
};

function DocStateChip({ state, id }) {
  const s = DOC_STATE_STYLE[state];
  if (!s) return null;
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" data-testid={`doc-state-${id}`}
          style={{ background: s.bg, color: s.fg }}>{s.label}</span>
  );
}

function CommentCard({ c }) {
  const kindColor = {
    change_request: { bg: '#fee2e2', fg: '#991b1b' },
    approve_doc: { bg: '#dcfce7', fg: '#166534' },
    note: { bg: '#f1f5f9', fg: '#334155' },
  }[c.kind] || { bg: '#f1f5f9', fg: '#334155' };
  return (
    <div className="rounded-lg border border-slate-100 p-3 bg-slate-50/50" data-testid={`comment-${c.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold" style={{ color: NAVY }}>{c.author_name}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: kindColor.bg, color: kindColor.fg }}>{c.kind.replace('_', ' ')}</span>
        <span className="text-[10px] text-slate-400 ml-auto">{formatDateTime(c.created_at)}</span>
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.comment}</p>
    </div>
  );
}
